import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import { toast, ToastContainer } from "react-toastify";
import {
  listAllSubjects,
  listAllChapters,
  getChapterSynopsis,
  apiErrorMessage,
} from "../../api/assessmentHierarchy";
import useDebouncedValue from "./useDebouncedValue";
import { buildSynopsisHtml, isSynopsisEmpty } from "./synopsisHtml";

const ACCENT = "#17479e";

// Read-only preview of the synopsis uploaded for a chapter. The content is admin-uploaded HTML, so it
// is rendered in a sandboxed iframe (no scripts, isolated styles).
function SynopsisViewer({ chapter }) {
  const [state, setState] = useState({ loading: false, data: null, error: "" });

  const load = () => {
    setState({ loading: true, data: null, error: "" });
    getChapterSynopsis(chapter.id)
      .then((data) => setState({ loading: false, data, error: "" }))
      .catch((err) =>
        setState({ loading: false, data: null, error: apiErrorMessage(err, "Failed to load synopsis") }),
      );
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapter.id]);

  const srcDoc = useMemo(
    () => (state.data && !isSynopsisEmpty(state.data) ? buildSynopsisHtml(state.data) : ""),
    [state.data],
  );

  if (state.loading) {
    return (
      <Box sx={{ m: "auto" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (state.error) {
    return (
      <Box sx={{ m: "auto", p: 3 }}>
        <Alert severity="error" action={<Button onClick={load}>Retry</Button>}>
          {state.error}
        </Alert>
      </Box>
    );
  }
  if (!srcDoc) {
    return <Typography sx={{ m: "auto", color: "text.secondary" }}>No synopsis available for this chapter</Typography>;
  }
  return (
    <iframe
      title="Chapter synopsis"
      sandbox=""
      srcDoc={srcDoc}
      style={{ flex: 1, border: 0, width: "100%", height: "100%" }}
    />
  );
}

function AssessmentSynopsis() {
  const [subjects, setSubjects] = useState([]);
  const [subjectId, setSubjectId] = useState("");
  const [chapters, setChapters] = useState([]);
  const [loadingChapters, setLoadingChapters] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    listAllSubjects()
      .then(setSubjects)
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to load subjects")));
  }, []);

  useEffect(() => {
    setLoadingChapters(true);
    listAllChapters({ subjectId })
      .then(setChapters)
      .catch((err) => {
        setChapters([]);
        toast.error(apiErrorMessage(err, "Failed to load chapters"));
      })
      .finally(() => setLoadingChapters(false));
  }, [subjectId]);

  const handleSubjectChange = (id) => {
    setSubjectId(id);
    setSelected(null);
    setSearch("");
  };

  const visibleChapters = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    const matches = q ? chapters.filter((c) => c.title?.toLowerCase().includes(q)) : chapters;
    return [...matches].sort((a, b) =>
      (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base", numeric: true }),
    );
  }, [chapters, debouncedSearch]);

  const subject = subjects.find((s) => s.id === subjectId);

  return (
    <Box sx={{ textAlign: "left" }}>
      <ToastContainer />

      <Box sx={{ display: "flex", gap: 2, flexWrap: { xs: "wrap", md: "nowrap" }, alignItems: "stretch" }}>
        <Paper
          sx={{
            width: { xs: "100%", md: 340 },
            flexShrink: 0,
            p: 2,
            boxShadow: 2,
            borderRadius: 3,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            height: { md: "calc(100vh - 130px)" },
            minHeight: 320,
          }}
        >
          <FormControl size="small" fullWidth>
            <InputLabel>Subject</InputLabel>
            <Select label="Subject" value={subjectId} onChange={(e) => handleSubjectChange(e.target.value)}>
              <MenuItem value="">All subjects</MenuItem>
              {subjects.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            placeholder="Search chapters"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "#999" }} />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            {loadingChapters ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : visibleChapters.length === 0 ? (
              <Typography sx={{ color: "text.secondary", fontSize: "0.85rem", p: 1 }}>No chapters found</Typography>
            ) : (
              <List dense disablePadding>
                {visibleChapters.map((c) => (
                  <ListItemButton
                    key={c.id}
                    selected={selected?.id === c.id}
                    disabled={!c.synopsisAvailable}
                    onClick={() => setSelected(c)}
                    sx={{ borderRadius: 2, mb: 0.25, minHeight: 40, py: 0.75 }}
                  >
                    <ListItemText
                      primary={c.title}
                      primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 600 }}
                    />
                    {!c.synopsisAvailable && (
                      <Chip label="No synopsis" size="small" variant="outlined" sx={{ fontSize: "0.65rem" }} />
                    )}
                  </ListItemButton>
                ))}
              </List>
            )}
          </Box>
        </Paper>

        <Paper
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            boxShadow: 2,
            borderRadius: 3,
            display: "flex",
            flexDirection: "column",
            height: { md: "calc(100vh - 130px)" },
            minHeight: 420,
          }}
        >
          {selected ? (
            <>
              <Box
                sx={{
                  p: 2,
                  flexShrink: 0,
                  color: "#fff",
                  background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <AutoStoriesOutlinedIcon />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: "1.05rem" }}>{selected.title}</Typography>
                  <Typography sx={{ fontSize: "0.78rem", opacity: 0.85 }}>
                    {selected.subjectNames.join(", ") || subject?.name || "—"} · Synopsis
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ flex: 1, minHeight: 0, display: "flex", backgroundColor: "#f7f8fb" }}>
                <SynopsisViewer chapter={selected} />
              </Box>
            </>
          ) : (
            <Typography sx={{ m: "auto", color: "text.secondary", p: 3 }}>
              Choose a chapter to view its synopsis
            </Typography>
          )}
        </Paper>
      </Box>
    </Box>
  );
}

export default AssessmentSynopsis;
